import React, { useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import DataTable from "@/components/DataTable";
import { StatusChip, DisplayChip, ArrivalCell, ReservedCell, FlagsCell, SerialCell, JobCell, ReceiveCell } from "@/components/spa/SpaUnitRow";

const opts = (pairs) => pairs.map(([value, label]) => ({ value, label }));
const FILTERS = [
  { type: "select", param: "category", label: "Category", options: opts(["Hot Tubs", "Swim Spas", "Saunas", "Cold Plunges", "Pergolas", "Covanas"].map((c) => [c, c])) },
  { type: "select", param: "status", label: "Status", options: opts([["in_stock", "In stock"], ["incoming", "Incoming"], ["arrival_overdue", "Arrival overdue"], ["not_ordered", "Not ordered"], ["on_order_no_date", "On order - no date"], ["status_unclear", "Status unclear"]]) },
  { type: "select", param: "location", label: "Location", options: opts([["warehouse", "Warehouse"], ["showroom", "Showroom"], ["on_the_way", "On the way"]]) },
  { type: "toggle", param: "missing_serial", label: "No serial number" },
];
const PICK_LABEL = { in_stock: "In stock", incoming: "Incoming", arrival_overdue: "Arrival overdue", not_ordered: "Not ordered" };

export function SpaUnitsTab({ filter, setFilter, onChanged }) {
  const { can } = useAuth();
  const canManage = can("stock.manage");
  const [rev, setRev] = useState(0);
  const act = async (fn, ok) => {
    try { await fn(); toast.success(ok); setRev((r) => r + 1); onChanged(); } catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };
  const cell = (C) => (v, u) => <C key={`${u.unit_id}-${rev}`} u={u} canManage={canManage} act={act} />;
  const columns = [
    { key: "title", label: "Unit", mandatory: true, strong: true, defaultWidth: 280, truncate: true,
      render: (v, u) => <Link to={`/products/${u.product_legacy_id}`} className="font-semibold text-indigo-700 hover:underline" data-testid={`spa-unit-product-link-${u.unit_id}`}>{v}</Link> },
    { key: "category", label: "Category", defaultWidth: 110 },
    { key: "colour", label: "Colour", defaultWidth: 180 },
    { key: "serial", label: "Serial", defaultWidth: 240, render: cell(SerialCell) },
    { key: "status_label", label: "Status", defaultWidth: 290, render: (v, u) => <StatusChip u={u} /> },
    { key: "location_label", label: "Location", defaultWidth: 170 },
    { key: "display_label", label: "Wet / dry", defaultWidth: 120, render: (v, u) => <DisplayChip u={u} /> },
    { key: "arrival", label: "Arrival", defaultWidth: 140, render: (v, u) => <ArrivalCell u={u} /> },
    { key: "reserved_for", label: "Reserved for", defaultWidth: 220, truncate: true, render: (v, u) => <ReservedCell u={u} /> },
    { key: "job_legacy_id", label: "Job", defaultWidth: 140, render: cell(JobCell) },
    { key: "po_label", label: "Supplier order", defaultWidth: 200 },
    { key: "spec", label: "Spec", defaultWidth: 150 },
    { key: "flags_text", label: "Needs checking", defaultWidth: 260, render: (v, u) => <FlagsCell u={u} /> },
    { key: "source_row", label: "Sheet row", defaultWidth: 90, defaultHidden: true },
    { key: "brand", label: "Brand", defaultWidth: 110 },
    { key: "supplier", label: "Supplier", defaultWidth: 140, defaultHidden: true },
    { key: "actions", label: "Receive", defaultWidth: 300, sortable: false, noExport: true, render: cell(ReceiveCell) },
  ];
  const params = filter;
  const chips = [filter.status && PICK_LABEL[filter.status], filter.holder === "free" && "Free to sell", filter.missing_serial && "No serial number"].filter(Boolean);
  return (
    <DataTable dataset="spa-units" tableId="spa_units" title="Hot tub units" columns={columns} filters={FILTERS} params={params} refreshKey={rev}
      hideHeader bare compactToggle searchPlaceholder="Model, serial, customer, job…"
      toolbarStart={chips.length > 0 && (
        <button type="button" onClick={() => setFilter({})} data-testid="spa-tile-filter-clear"
          className="inline-flex h-9 items-center gap-1 rounded-lg border border-amber-300 bg-amber-50 px-3 text-sm font-medium text-amber-800">
          {chips.join(" · ")} <X className="w-3.5 h-3.5" /></button>)} />
  );
}
